import { LineChart, ScatterChart } from "echarts/charts";
import { GridComponent, LegendComponent, TooltipComponent } from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { useCallback, useMemo } from "react";
import { IdeaSource } from "../types";
echarts.use([GridComponent, LegendComponent, TooltipComponent, LineChart, ScatterChart, CanvasRenderer]);
const useIdeasChart = ({ data, humanColor, machineColor, paretoColor, isNarrow, renderTooltip }) => {
    const pointKey = useCallback((score1, score2) => JSON.stringify([score1, score2]), []);
    return useMemo(() => {
        const ideas = data?.ideas ?? [];
        const paretoFront = data?.pareto_front ?? [];
        const xLabel = data?.objectives.objective1;
        const yLabel = data?.objectives.objective2;
        const groupedByKey = new Map();
        ideas.forEach((idea, index) => {
            const key = pointKey(idea.score1, idea.score2);
            const point = {
                idea: idea.idea,
                ideaNumber: index + 1,
                source: idea.source,
                feedback: idea.feedback,
                score1: idea.score1,
                score2: idea.score2,
                isLatest: idea.is_latest,
            };
            const existing = groupedByKey.get(key);
            if (existing) {
                existing.points.push(point);
                existing.sources.add(idea.source);
                return;
            }
            groupedByKey.set(key, {
                score1: idea.score1,
                score2: idea.score2,
                points: [point],
                sources: new Set([idea.source]),
            });
        });
        const toDatum = (group) => {
            const isMixed = group.sources.size > 1;
            const hasLatest = group.points.some((point) => point.isLatest);
            const symbolSize = hasLatest ? 14 : 10;
            const itemStyle = {};
            if (isMixed) {
                itemStyle["color"] = new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                    { offset: 0, color: humanColor },
                    { offset: 0.5, color: humanColor },
                    { offset: 0.5, color: machineColor },
                    { offset: 1, color: machineColor },
                ]);
            }
            return {
                value: [group.score1, group.score2],
                symbolSize,
                itemStyle,
            };
        };
        const humanData = [];
        const machineData = [];
        const mixedData = [];
        groupedByKey.forEach((group) => {
            const datum = toDatum(group);
            if (group.sources.size > 1) {
                // Mixed-point color is controlled at series level so legend toggles can recolor it dynamically.
                delete datum.itemStyle["color"];
                mixedData.push(datum);
            }
            else if (group.sources.has(IdeaSource.HUMAN)) {
                humanData.push(datum);
            }
            else {
                machineData.push(datum);
            }
        });
        const paretoLine = [...paretoFront]
            .sort((a, b) => a.score1 - b.score1)
            .map((point) => [point.score1, point.score2]);
        const paretoData = paretoFront.map((point) => {
            const key = pointKey(point.score1, point.score2);
            const group = groupedByKey.get(key);
            const symbolSize = group?.points.some((candidate) => candidate.isLatest) ? 14 : 10;
            return {
                value: [point.score1, point.score2],
                symbolSize,
                itemStyle: {},
            };
        });
        return {
            grid: {
                top: isNarrow ? 56 : 48,
                right: isNarrow ? 40 : 56,
                bottom: isNarrow ? 120 : 104,
                left: isNarrow ? 64 : 84,
                containLabel: true,
            },
            legend: {
                // The grid reserves a single legend row, so paging beats wrapping: a second
                // row would otherwise be drawn on top of the plot on narrow charts.
                type: "scroll",
                top: 8,
                left: "center",
                // echarts centers the ~12px marker circles inside itemWidth, so a wide box
                // strands space before each label; itemGap must stay well past itemWidth.
                itemWidth: 20,
                itemHeight: 12,
                itemGap: 28,
                // Legend order is set here rather than by series order, which controls draw order.
                data: [{ name: "Human Ideas" }, { name: "AI Ideas" }, { name: "Pareto Ideas" }, { name: "Pareto Line" }],
            },
            xAxis: {
                type: "value",
                name: xLabel,
                min: 0,
                max: 100,
                nameLocation: "middle",
                nameGap: 36,
            },
            yAxis: {
                type: "value",
                name: yLabel,
                min: 0,
                max: 100,
                nameLocation: "middle",
                nameGap: 50,
            },
            tooltip: {
                renderMode: "html",
                appendToBody: true,
                confine: true,
                // Stacked ideas can overflow the card's height cap, so the pointer has to be
                // able to enter the tooltip and scroll it.
                enterable: true,
                trigger: "item",
                // `appendToBody` widens the box `confine` clamps against from the chart to the
                // whole page, so a tall card runs off the bottom of the screen unless it is
                // clamped to the chart's own bounds here.
                position: (point, _params, _dom, _rect, size) => {
                    const [viewWidth, viewHeight] = size.viewSize;
                    const [tooltipWidth, tooltipHeight] = size.contentSize;
                    const gap = 12;
                    const clamp = (value, limit) => Math.max(0, Math.min(value, limit));
                    return [
                        clamp(point[0] + gap, viewWidth - tooltipWidth),
                        clamp(point[1] + gap, viewHeight - tooltipHeight),
                    ];
                },
                // The tooltip content renders its own card, so echarts' default box is
                // stripped back to nothing to avoid a frame around a frame. echarts also
                // hardcodes `white-space: nowrap` on its container, which would stop the
                // card's text from wrapping; `extraCssText` is applied last, so it wins.
                padding: 0,
                backgroundColor: "transparent",
                borderWidth: 0,
                extraCssText: "box-shadow: none; border-radius: 0; white-space: normal;",
                formatter: (params) => {
                    const param = Array.isArray(params) ? params[0] : params;
                    if (!param || typeof param !== "object") {
                        return "";
                    }
                    const value = "value" in param ? param.value : undefined;
                    // A scatter point's value is [x, y]; both are needed below.
                    if (!Array.isArray(value) || value.length < 2) {
                        return "";
                    }
                    const [x, y] = value;
                    if (typeof x !== "number" || typeof y !== "number") {
                        return "";
                    }
                    const key = pointKey(x, y);
                    const points = groupedByKey.get(key)?.points ?? [];
                    // The labels only widen the guard for the type checker: a grouped point
                    // cannot exist without the chart data the objectives come from.
                    if (points.length === 0 || !xLabel || !yLabel) {
                        return "";
                    }
                    return renderTooltip({
                        x,
                        y,
                        xLabel,
                        yLabel,
                        points,
                    });
                },
            },
            dataZoom: [
                {
                    type: "slider",
                    xAxisIndex: 0,
                    bottom: isNarrow ? 52 : 40,
                },
                {
                    type: "slider",
                    yAxisIndex: 0,
                    right: isNarrow ? 4 : 8,
                },
            ],
            series: [
                {
                    name: "Human Ideas",
                    type: "scatter",
                    data: humanData,
                    itemStyle: { color: humanColor },
                },
                {
                    name: "AI Ideas",
                    type: "scatter",
                    data: machineData,
                    itemStyle: { color: machineColor },
                },
                {
                    id: "mixed-ideas-series",
                    name: "Mixed Ideas",
                    type: "scatter",
                    data: mixedData,
                    itemStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                            { offset: 0, color: humanColor },
                            { offset: 0.5, color: humanColor },
                            { offset: 0.5, color: machineColor },
                            { offset: 1, color: machineColor },
                        ]),
                    },
                },
                {
                    name: "Pareto Ideas",
                    type: "scatter",
                    data: paretoData,
                    symbol: "circle",
                    itemStyle: {
                        color: "rgba(0, 0, 0, 0)",
                        borderColor: paretoColor,
                        borderWidth: 2,
                    },
                    silent: true,
                    tooltip: { show: false },
                },
                {
                    name: "Pareto Line",
                    type: "line",
                    data: paretoLine,
                    symbol: "none",
                    lineStyle: { color: paretoColor, width: 2 },
                    silent: true,
                    tooltip: { show: false },
                },
            ],
        };
    }, [data, humanColor, machineColor, paretoColor, isNarrow, pointKey, renderTooltip]);
};
export default useIdeasChart;
